import { useEffect, useRef, useState } from "react";

import { onboardingPublicAPI, type CompanyLookupResult, type LeadProfile } from "../OnboardingAPI";

/** The pause after the LAST change of the company name before the address is looked up (a blur does not shorten it). */
export const COMPANY_LOOKUP_DELAY_MS = 3000;
/** The shortest name that is looked up. */
export const COMPANY_LOOKUP_MIN_LENGTH = 3;
/** How long the fields an adopted suggestion filled are highlighted. */
export const LOOKUP_HIGHLIGHT_MS = 600;

export type LookupSuggestion = NonNullable<CompanyLookupResult["suggestion"]>;
export type LookupPhase = "idle" | "waiting" | "searching" | "found";

const ADDRESS_FIELDS = ["street", "streetNumber", "postalCode", "city"] as const;
const FIELDS = ["legalName", "street", "streetNumber", "postalCode", "city", "country", "website"] as const;

/** The server may say "DE": the page's country field holds the name. */
const countryName = (value: string | undefined): string | undefined => {
  const text = (value ?? "").trim();
  if (!text) return undefined;
  return text.toUpperCase() === "DE" ? "Deutschland" : text;
};

/** Only a web address that is http or https may become a link. */
export const safeSourceUrl = (url: string | undefined): string | null => {
  try {
    const parsed = new URL(url ?? "");
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
};

/**
 * Looks an address up from the company name, quietly: 3 s after the CUSTOMER last changed the name (an input event: the
 * name the invitation prefilled, or a reload, starts nothing; not before, not on blur),
 * only while it has at least 3 characters and the address is still empty, once per different name in this visit, never
 * again for a name the person said no to, and never when the step is left or the form is sent (the effect is torn down).
 * Every change of the name starts the wait anew; an answer for an older name is thrown away. "Searching ..." shows only
 * when the request really starts. Nothing is shown for "not found", "unavailable" or any error.
 */
export function useAddressLookup({
  enabled,
  token,
  companyName,
  profile,
  update,
}: {
  enabled: boolean;
  token: string;
  companyName: string;
  profile: LeadProfile;
  update: (field: keyof LeadProfile, value: string) => void;
}) {
  const [phase, setPhase] = useState<LookupPhase>("idle");
  const [suggestion, setSuggestion] = useState<{ data: LookupSuggestion; source?: CompanyLookupResult["source"] } | null>(null);
  const [highlighted, setHighlighted] = useState<string[]>([]);
  const asked = useRef(new Set<string>());
  // the name as it was when the page put it in (the invitation's, or the saved one): nothing is looked up for it until the customer changes it
  const initial = useRef<string | null>(null);
  if (initial.current === null && companyName !== undefined) initial.current = companyName.trim().toLowerCase();
  const [touched, setTouched] = useState(false);
  const declined = useRef(new Set<string>());
  const highlightTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(highlightTimer.current), []);

  const name = companyName.trim();
  const addressEmpty = ADDRESS_FIELDS.every((field) => !(profile[field] ?? "").trim());
  // typed by the customer: the name is not the prefilled one any more (typing it back to the prefilled one does not count)
  const typed = touched && name.toLowerCase() !== initial.current;
  const eligible = enabled && typed && name.length >= COMPANY_LOOKUP_MIN_LENGTH && addressEmpty;

  // every change of the name (or of what makes it eligible) starts again from nothing
  useEffect(() => {
    setSuggestion(null);
    const key = name.toLowerCase();
    // once per different name, and never again for one the person said no to
    if (!eligible || asked.current.has(key) || declined.current.has(key)) {
      setPhase("idle");
      return;
    }
    setPhase("waiting");
    let stale = false;
    const timer = window.setTimeout(() => {
      asked.current.add(key);
      setPhase("searching");
      Promise.resolve()
        .then(() => onboardingPublicAPI.companyLookup(token, name))
        .then((result) => {
          if (stale) return;
          if (result?.status === "found" && result.suggestion && FIELDS.some((field) => (result.suggestion![field] ?? "").trim())) {
            setSuggestion({ data: result.suggestion, source: result.source });
            setPhase("found");
          } else setPhase("idle");
        })
        // a refusal, a limit or a failure: nothing to show
        .catch(() => {
          if (!stale) setPhase("idle");
        });
    }, COMPANY_LOOKUP_DELAY_MS);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, eligible, token]);

  /** Takes the suggestion over - into empty fields only - and highlights what was filled. */
  const adopt = () => {
    if (!suggestion) return;
    const filled: string[] = [];
    FIELDS.forEach((field) => {
      const value = field === "country" ? countryName(suggestion.data.country) : suggestion.data[field]?.trim();
      if (value && !(profile[field] ?? "").trim()) {
        update(field, value);
        filled.push(field);
      }
    });
    setSuggestion(null);
    setPhase("idle");
    setHighlighted(filled);
    window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => setHighlighted([]), LOOKUP_HIGHLIGHT_MS);
  };

  /** "No, I enter it myself": the card goes, and this name is not looked up again. */
  const decline = () => {
    declined.current.add(name.toLowerCase());
    setSuggestion(null);
    setPhase("idle");
  };

  /** The customer typed in the company name field. */
  const onNameInput = () => setTouched(true);

  return { phase, suggestion, highlighted, adopt, decline, onNameInput };
}
